import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import { TreeItem, TreeView } from "@mui/x-tree-view";
import useGlobalContext from "hooks/globalData";
import { FormattedMessage as T } from "react-intl";
import Loader from "components/Loader";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { useEffect, useMemo, useState } from "react";
import { sortBy } from "lodash";
import { Claim } from "services/KnowfullyAdminClient";
import { useStrictQuery } from "hooks/useStrictQuery";
import {
  calculateInitialExpandedClaimNodes,
  createClaimNodeId,
  getGroupsWithSelectedClaims,
  groupClaimsByApplication,
} from "helpers/ClaimHelpers";

type ClaimSelectorProps = {
  selectedIds: string[];
  onChange?: (ids: string[]) => void;
  disabled?: boolean;
  isCreateMode?: boolean;
};

const ClaimSelector = ({
  selectedIds,
  onChange,
  disabled,
  isCreateMode = false,
}: ClaimSelectorProps) => {
  const [selected, setSelected] = useState<string[]>(selectedIds ?? []);
  const { claimService } = useGlobalContext();
  const { isLoading, data } = useStrictQuery(["claims"], () =>
    claimService.getClaims()
  );

  const claims = useMemo(() => data?.data ?? [], [data?.data]);
  const claimsByApplication = useMemo(() => groupClaimsByApplication(claims), [claims]);
  const applicationNames = useMemo(
    () => Object.keys(claimsByApplication).sort((a, b) => a.localeCompare(b)),
    [claimsByApplication]
  );

  const [expanded, setExpanded] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (initialized || applicationNames.length === 0) return;

    const selectedClaimIds = new Set(selectedIds ?? []);
    const initialExpanded = calculateInitialExpandedClaimNodes(
      claimsByApplication,
      selectedClaimIds,
      isCreateMode
    );

    setExpanded(initialExpanded);
    setInitialized(true);
  }, [applicationNames, claimsByApplication, initialized, selectedIds, isCreateMode]);

  useEffect(() => {
    setSelected(selectedIds ?? []);
  }, [selectedIds]);

  const updateSelection = (newSelected: string[]) => {
    setSelected(newSelected);
    if (onChange && !disabled) {
      onChange(newSelected);
    }
  };

  if (isLoading) return <Loader />;

  const handleSelect = (
    claimId: string,
    groupClaims: Claim[],
    claimSequence?: number
  ) => {
    const lowerSequenceClaims = groupClaims.filter(
      (c) =>
        c.id &&
        c.sequence !== undefined &&
        claimSequence !== undefined &&
        c.sequence < claimSequence
    );
    const newIds = [
      claimId,
      ...lowerSequenceClaims.map((c) => c.id!),
    ].filter((id) => !selected.includes(id));
    updateSelection([...selected, ...newIds]);
  };

  const handleUnselect = (claimId: string) => {
    updateSelection(selected.filter((id) => id !== claimId));
  };

  const onNodeToggle = (_: React.SyntheticEvent, nodeIds: string[]) => {
    const newlyExpandedApps = nodeIds
      .filter((id) => !expanded.includes(id) && id.startsWith("app-"));

    if (newlyExpandedApps.length === 0) {
      setExpanded(nodeIds);
      return;
    }

    const selectedClaimIds = new Set(selected);
    const groupsToExpand = newlyExpandedApps.flatMap((appId) => {
      const appName = appId.replace("app-", "");
      const appGroups = claimsByApplication[appName];
      if (!appGroups) return [];

      return getGroupsWithSelectedClaims(appGroups, selectedClaimIds)
        .map((groupName) => createClaimNodeId.group(appName, groupName));
    });

    setExpanded(Array.from(new Set([...nodeIds, ...groupsToExpand])));
  };

  const hasActiveNeighborWithHigherSequence = (
    groupClaims: Claim[],
    claimSequence?: number
  ) => {
    return groupClaims.some(
      (c) =>
        c.sequence !== undefined &&
        claimSequence !== undefined &&
        c.sequence > claimSequence &&
        c.id &&
        selected.includes(c.id)
    );
  };

  const renderClaimCheckbox = (claim: Claim, groupClaims: Claim[]) => {
    const isDisabled =
      disabled ||
      hasActiveNeighborWithHigherSequence(groupClaims, claim.sequence);
    const isChecked = claim.id ? selected.includes(claim.id) : false;

    const handleRowClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!isDisabled && claim.id) {
        if (isChecked) {
          handleUnselect(claim.id);
        } else {
          handleSelect(claim.id, groupClaims, claim.sequence);
        }
      }
    };

    return (
      <TreeItem
        key={claim.id}
        nodeId={createClaimNodeId.claim(claim.id ?? "")}
        label={
          <Box onClick={handleRowClick} sx={{ cursor: isDisabled ? "default" : "pointer" }}>
            <FormControlLabel
              control={
                <Checkbox
                  size="small"
                  disabled={isDisabled}
                  checked={isChecked}
                  onClick={(e) => e.stopPropagation()}
                />
              }
              label={claim.name}
            />
          </Box>
        }
      />
    );
  };

  const renderClaimsFlat = (claims: Claim[]) => {
    const sortedClaims = sortBy(claims, (c) => c.sequence ?? 0);
    return sortedClaims.map((claim) => renderClaimCheckbox(claim, sortedClaims));
  };

  const renderGroup = (appName: string, groupName: string, groupClaims: Claim[]) => {
    const sortedClaims = sortBy(groupClaims, (c) => c.sequence ?? 0);

    return (
      <TreeItem
        key={createClaimNodeId.group(appName, groupName)}
        nodeId={createClaimNodeId.group(appName, groupName)}
        label={<Box m={0.5}>{groupName}</Box>}
      >
        {sortedClaims.map((claim) => renderClaimCheckbox(claim, sortedClaims))}
      </TreeItem>
    );
  };

  const renderApplication = (appName: string) => {
    const appGroups = claimsByApplication[appName];
    if (!appGroups) return null;

    const { grouped, ungrouped } = appGroups;
    const sortedGroupNames = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

    return (
      <TreeItem
        key={createClaimNodeId.app(appName)}
        nodeId={createClaimNodeId.app(appName)}
        label={<Box m={0.5}>{appName}</Box>}
      >
        {sortedGroupNames.map((groupName) =>
          renderGroup(appName, groupName, grouped[groupName])
        )}
        {ungrouped.length > 0 && renderClaimsFlat(ungrouped)}
      </TreeItem>
    );
  };

  return (
    <Box mt={4}>
      <Typography component="h6">
        <T id="claims" />
      </Typography>
      <TreeView
        expanded={expanded}
        onNodeToggle={onNodeToggle}
        defaultCollapseIcon={<ExpandMoreIcon />}
        defaultExpandIcon={<ChevronRightIcon />}
      >
        <hr />
        {applicationNames.map((appName) => (
          <Box key={appName}>
            {renderApplication(appName)}
            <hr />
          </Box>
        ))}
      </TreeView>
    </Box>
  );
};

export default ClaimSelector;
